import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  IconButton,
  Stack,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Autocomplete
} from '@mui/material';
import {
  RadioTwoTone,
  Download,
  Person,
  Save,
  Add,
  Delete,
  MusicNote,
  Instagram,
  Twitter,
  Facebook,
  Language,
  Schedule,
  LocationOn,
  Edit,
  Dashboard as DashboardIcon,
  Podcasts,
  Settings,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import api from '../../utils/axios';
import ProfileImageUploader from '../common/ProfileImageUploader';
import EmptyState from '../common/EmptyState';
import DashboardLayout from '../layout/DashboardLayout';

// Genre options for the dropdown
const genreOptions = [
  'House', 'Techno', 'EDM', 'Hip Hop', 'R&B', 'Top 40', 'Latin', 'Reggaeton',
  'Dancehall', 'Pop', 'Rock', 'Indie', 'Disco', 'Funk', 'Soul', 'Jazz',
  'World', 'Ambient', 'Lounge', 'Other'
];

const SECTIONS = [
  { key: 'overview', label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
  { key: 'stream', label: 'Stream', icon: <Podcasts fontSize="small" /> },
  { key: 'schedule', label: 'Schedule', icon: <Schedule fontSize="small" /> },
  { key: 'profile', label: 'Profile Settings', icon: <Settings fontSize="small" /> },
];

const DJDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectedVenues, setConnectedVenues] = useState([]);
  const [error, setError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [socket, setSocket] = useState(null);
  const [userOS, setUserOS] = useState('unknown');
  const [isMobile, setIsMobile] = useState(false);

  // DJ Profile state
  const [profile, setProfile] = useState({
    name: '', email: '', genre: '', bio: '', profileImage: '',
    yearsExperience: 0, equipment: [],
    availability: { monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false },
    schedule: [], featuredTracks: [],
    socialLinks: { instagram: '', twitter: '', facebook: '', soundcloud: '', mixcloud: '', website: '' },
    djDetails: { mountCount: 0 }
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Schedule dialog state
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleEntry, setScheduleEntry] = useState({ venue: '', venueId: '', day: '', startTime: '', endTime: '', notes: '' });
  const [editIndex, setEditIndex] = useState(-1);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [venueOptions, setVenueOptions] = useState([]);
  const [venueLoading, setVenueLoading] = useState(false);

  // Equipment management
  const [newEquipment, setNewEquipment] = useState('');
  // Featured tracks management
  const [newTrack, setNewTrack] = useState({ title: '', url: '' });

  useEffect(() => {
    const detectOS = () => {
      const userAgent = window.navigator.userAgent;
      const platform = window.navigator.platform;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      if (mobileRegex.test(userAgent)) { setIsMobile(true); setUserOS('mobile'); return; }
      if (platform.indexOf('Mac') !== -1) setUserOS('mac');
      else if (platform.indexOf('Win') !== -1) setUserOS('windows');
      else if (platform.indexOf('Linux') !== -1) setUserOS('linux');
      else setUserOS('unknown');
    };

    detectOS();
    const user = JSON.parse(localStorage.getItem('user'));
    fetchProfile();

    const newSocket = io('http://localhost:5000', { auth: { token: localStorage.getItem('token') } });
    newSocket.on('connect', () => console.log('Connected to server'));
    newSocket.on('venue-connected', (venue) => setConnectedVenues(prev => [...prev, venue]));
    newSocket.on('venue-disconnected', (venueId) => setConnectedVenues(prev => prev.filter(v => v.id !== venueId)));
    newSocket.on('stream-status', (data) => { if (data.djId === user?.id) setIsStreaming(data.isActive); });
    newSocket.on('connect', () => newSocket.emit('check-stream-status', { djId: user?.id }));
    setSocket(newSocket);
    return () => newSocket.close();
  }, []);

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const response = await api.get('/users/profile');
      const userData = response.data;
      setProfile({
        name: userData.name || '', email: userData.email || '',
        genre: userData.djDetails?.genre || '', bio: userData.djDetails?.bio || '',
        profileImage: userData.djDetails?.profileImage || '',
        yearsExperience: userData.djDetails?.yearsExperience || 0,
        equipment: userData.djDetails?.equipment || [],
        availability: userData.djDetails?.availability || { monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false },
        schedule: userData.djDetails?.schedule || [],
        featuredTracks: userData.djDetails?.featuredTracks || [],
        socialLinks: {
          instagram: userData.djDetails?.socialLinks?.instagram || '',
          twitter: userData.djDetails?.socialLinks?.twitter || '',
          facebook: userData.djDetails?.socialLinks?.facebook || '',
          soundcloud: userData.djDetails?.socialLinks?.soundcloud || '',
          mixcloud: userData.djDetails?.socialLinks?.mixcloud || '',
          website: userData.djDetails?.socialLinks?.website || ''
        },
        djDetails: { mountCount: userData.djDetails?.mountCount || 0 }
      });
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setNotification({ open: true, message: 'Failed to load profile data', severity: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value, checked } = e.target;
    if (name.startsWith('social_')) {
      const socialField = name.split('_')[1];
      setProfile(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, [socialField]: value } }));
    } else if (name.startsWith('availability_')) {
      const day = name.split('_')[1];
      setProfile(prev => ({ ...prev, availability: { ...prev.availability, [day]: checked } }));
    } else if (name === 'yearsExperience') {
      setProfile(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setProfile(prev => ({ ...prev, [name]: value }));
    }
  };

  // Schedule handling
  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    setScheduleEntry(prev => ({ ...prev, [name]: value }));
  };

  const handleVenueSearch = async (query) => {
    setVenueSearchQuery(query);
    if (query.length < 2) { setVenueOptions([]); return; }
    try {
      setVenueLoading(true);
      const response = await api.get(`/users/venues/search?name=${encodeURIComponent(query)}`);
      setVenueOptions(response.data);
    } catch (err) {
      console.error('Error searching venues:', err);
      setNotification({ open: true, message: 'Failed to search venues', severity: 'error' });
    } finally { setVenueLoading(false); }
  };

  const handleVenueSelect = (venue) => {
    if (!venue || !venue._id) {
      setNotification({ open: true, message: 'Invalid venue selection. Please try again.', severity: 'error' });
      return;
    }
    setScheduleEntry(prev => ({ ...prev, venue: venue.name, venueId: venue._id }));
    setVenueOptions([]);
    setVenueSearchQuery('');
  };

  const handleOpenScheduleDialog = (index = -1) => {
    if (index >= 0) { setScheduleEntry({...profile.schedule[index]}); setEditIndex(index); }
    else { setScheduleEntry({ venue: '', venueId: '', day: '', startTime: '', endTime: '', notes: '' }); setEditIndex(-1); }
    setScheduleDialog(true);
  };

  const handleCloseScheduleDialog = () => { setScheduleDialog(false); setVenueOptions([]); setVenueSearchQuery(''); };

  const handleSaveSchedule = async () => {
    if (!scheduleEntry.venue || !scheduleEntry.day) {
      setNotification({ open: true, message: 'Venue and day are required', severity: 'error' });
      return;
    }

    const newEntry = { ...scheduleEntry };
    let updatedSchedule;
    if (editIndex >= 0) {
      updatedSchedule = [...profile.schedule];
      updatedSchedule[editIndex] = newEntry;
    } else {
      updatedSchedule = [...profile.schedule, newEntry];
    }

    setProfile(prev => ({ ...prev, schedule: updatedSchedule }));
    setScheduleDialog(false);
    setVenueOptions([]);
    setVenueSearchQuery('');

    try {
      // TODO: Backend should expose a dedicated schedule endpoint (e.g. POST /users/profile/schedule).
      // For now, send only the schedule payload to the profile endpoint.
      const schedulePayload = { djDetails: { schedule: updatedSchedule } };
      const response = await api.patch(`/users/profile/schedule?t=${Date.now()}`, schedulePayload).catch(() => {
        // Fallback: if the dedicated endpoint doesn't exist yet, use the general profile endpoint
        return api.patch(`/users/profile?t=${Date.now()}`, schedulePayload);
      });

      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data, djDetails: { ...prev.djDetails, ...response.data.djDetails } }));
      }
      setNotification({ open: true, message: 'Schedule updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to save schedule:', err);
      setNotification({ open: true, message: 'Failed to save schedule', severity: 'error' });
      fetchProfile();
    }
  };

  const handleDeleteScheduleEntry = async (indexToDelete) => {
    try {
      const updatedSchedule = profile.schedule.filter((_, index) => index !== indexToDelete);
      setProfile(prev => ({ ...prev, schedule: updatedSchedule }));

      // TODO: Use dedicated schedule endpoint when available
      const schedulePayload = { djDetails: { schedule: updatedSchedule } };
      const response = await api.patch(`/users/profile?t=${Date.now()}`, schedulePayload);
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data, djDetails: { ...prev.djDetails, ...response.data.djDetails } }));
      }
      setNotification({ open: true, message: 'Schedule entry deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete schedule entry:', err);
      setNotification({ open: true, message: 'Failed to delete schedule entry', severity: 'error' });
      fetchProfile();
    }
  };

  const handleAddEquipment = () => {
    if (newEquipment.trim()) {
      setProfile(prev => ({ ...prev, equipment: [...prev.equipment, newEquipment.trim()] }));
      setNewEquipment('');
    }
  };

  const handleRemoveEquipment = (index) => {
    setProfile(prev => ({ ...prev, equipment: prev.equipment.filter((_, i) => i !== index) }));
  };

  const handleAddTrack = () => {
    if (newTrack.title.trim() && newTrack.url.trim()) {
      setProfile(prev => ({ ...prev, featuredTracks: [...prev.featuredTracks, { ...newTrack }] }));
      setNewTrack({ title: '', url: '' });
    }
  };

  const handleRemoveTrack = (index) => {
    setProfile(prev => ({ ...prev, featuredTracks: prev.featuredTracks.filter((_, i) => i !== index) }));
  };

  const handleTrackChange = (e) => {
    const { name, value } = e.target;
    setNewTrack(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      const updateData = {
        name: profile.name,
        djDetails: {
          genre: profile.genre, bio: profile.bio,
          yearsExperience: profile.yearsExperience, equipment: profile.equipment,
          availability: profile.availability, schedule: profile.schedule,
          featuredTracks: profile.featuredTracks, socialLinks: profile.socialLinks
        }
      };
      const response = await api.patch(`/users/profile?t=${Date.now()}`, updateData);
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data, djDetails: { ...prev.djDetails, ...response.data.djDetails } }));
      }
      setNotification({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to update profile:', err);
      setNotification({ open: true, message: 'Failed to update profile', severity: 'error' });
    }
  };

  const handleCloseNotification = () => setNotification(prev => ({ ...prev, open: false }));

  const handleDownloadButtConfig = async () => {
    try {
      setDownloadError('');
      setProfileLoading(true);
      const response = await api.get('/users/butt-config', { responseType: 'blob' });
      if (response.data.size === 0) throw new Error('Received empty file from server');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'SetSync_butt_config.cfg');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setNotification({ open: true, message: 'B.U.T.T. configuration downloaded successfully', severity: 'success' });
    } catch (err) {
      console.error('Download error:', err);
      if (err.response) setDownloadError(`Failed to download B.U.T.T. configuration: ${err.response.data.error || err.response.statusText}`);
      else if (err.request) setDownloadError('Failed to download B.U.T.T. configuration: No response from server');
      else setDownloadError(`Failed to download B.U.T.T. configuration: ${err.message}`);
    } finally { setProfileLoading(false); }
  };

  // ---- Section Renderers ----

  const renderOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: 'primary.main' } }}>
          <Typography variant="h6" gutterBottom>Stream Status</Typography>
          {isStreaming ? (
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(0, 217, 126, 0.1)', border: '1px solid', borderColor: 'success.main', py: 1, px: 2, mb: 2 }}>
              <RadioTwoTone sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="body2" fontWeight="medium">You are currently streaming</Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Not currently streaming.</Typography>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: 'primary.main' } }}>
          <Typography variant="h6" gutterBottom>Connected Venues ({connectedVenues.length})</Typography>
          {connectedVenues.length === 0 ? (
            <EmptyState
              title="No Connected Venues"
              message="Venues will appear here when they connect to your stream."
            />
          ) : (
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {connectedVenues.map((venue) => (
                <Card key={venue.id} sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <RadioTwoTone color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2">{venue.name}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      </Grid>
      {/* Quick schedule preview */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Upcoming Schedule</Typography>
          {profile.schedule.length === 0 ? (
            <EmptyState
              title="No Schedule Yet"
              message="Add your regular gigs to help venues find you."
              ctaText="Add Schedule"
              onCtaClick={() => { setActiveSection('schedule'); handleOpenScheduleDialog(); }}
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Venue</TableCell><TableCell>Day</TableCell><TableCell>Time</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {profile.schedule.slice(0, 5).map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.venue}</TableCell>
                      <TableCell>{item.day.charAt(0).toUpperCase() + item.day.slice(1)}</TableCell>
                      <TableCell>{item.startTime}{item.endTime ? ` - ${item.endTime}` : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  const renderStream = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Stream Controls</Typography>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          {downloadError && <Typography color="error" sx={{ mb: 2 }}>{downloadError}</Typography>}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadButtConfig}>
              Download B.U.T.T. Config
            </Button>
          </Box>
          {profile.djDetails?.mountCount === 0 && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: 1, borderColor: 'primary.main', bgcolor: 'rgba(255, 107, 26, 0.1)' }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                You haven't started streaming yet!
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                To stream your sets, you need to download and install BUTT (Broadcast Using This Tool) software.
              </Typography>
              {isMobile ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                    It looks like you're on a mobile device. Please download BUTT on the computer where your turntable/controller is connected.
                  </Typography>
                  <Link href="https://danielnoethen.de/butt/" target="_blank" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                    Visit the BUTT website
                  </Link>
                </Box>
              ) : (
                <Button variant="contained" color="secondary"
                  href={userOS === 'mac' ? "https://danielnoethen.de/butt/release/1.45.0/butt-1.45.0.dmg" : userOS === 'windows' ? "https://danielnoethen.de/butt/release/1.45.0/butt-1.45.0-setup.exe" : "https://danielnoethen.de/butt/"}
                  target="_blank" size="small" sx={{ mt: 1 }}
                >
                  Download BUTT for {userOS === 'mac' ? 'Mac' : userOS === 'windows' ? 'Windows' : 'Your Device'}
                </Button>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                After installing, return here to download your configuration file.
              </Typography>
            </Paper>
          )}
          {isStreaming && (
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(0, 217, 126, 0.1)', border: '1px solid', borderColor: 'success.main', py: 1, px: 2, mb: 2 }}>
              <RadioTwoTone sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="body2" fontWeight="medium">You are currently streaming</Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  const renderSchedule = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Schedule sx={{ mr: 1 }} />
          <Typography variant="h6">Regular Schedule</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => handleOpenScheduleDialog()}>
          Add Schedule
        </Button>
      </Box>

      {profile.schedule.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Venue</TableCell><TableCell>Day</TableCell><TableCell>Time</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {profile.schedule.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      {item.venue}
                    </Box>
                  </TableCell>
                  <TableCell>{item.day.charAt(0).toUpperCase() + item.day.slice(1)}</TableCell>
                  <TableCell>{item.startTime}{item.endTime ? ` - ${item.endTime}` : ''}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenScheduleDialog(index)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteScheduleEntry(index)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon={<Schedule />}
          title="No Schedule Yet"
          message="Add your regular gigs to help venues find you when they need DJs."
          ctaText="Add Your First Gig"
          onCtaClick={() => handleOpenScheduleDialog()}
        />
      )}
    </Box>
  );

  const renderProfile = () => (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Person sx={{ mr: 1 }} />
        <Typography variant="h6">DJ Profile</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {profileLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ProfileImageUploader
              currentImage={profile.profileImage}
              onImageUpdate={(newImagePath) => {
                setProfile(prev => ({ ...prev, profileImage: newImagePath }));
                setTimeout(() => setNotification({ open: true, message: 'Profile image updated successfully', severity: 'success' }), 300);
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Name" name="name" value={profile.name} onChange={handleProfileChange} margin="normal" disabled variant="outlined" />
            <TextField fullWidth label="Email" name="email" value={profile.email} disabled margin="normal" variant="outlined" />
            <FormControl fullWidth margin="normal">
              <InputLabel>Genre</InputLabel>
              <Select name="genre" value={profile.genre} onChange={handleProfileChange} label="Genre">
                {genreOptions.map(genre => (<MenuItem key={genre} value={genre}>{genre}</MenuItem>))}
              </Select>
            </FormControl>
            <TextField fullWidth label="Years of Experience" name="yearsExperience" type="number"
              value={profile.yearsExperience} onChange={handleProfileChange} margin="normal" variant="outlined" inputProps={{ min: 0 }} />
          </Grid>

          {/* Biography */}
          <Grid item xs={12} md={8}>
            <TextField fullWidth multiline rows={4} label="Biography" name="bio" value={profile.bio}
              onChange={handleProfileChange} placeholder="Tell venues about yourself, your style, and your experience..." margin="normal" variant="outlined" />

            {/* Equipment */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Equipment</Typography>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <TextField fullWidth size="small" label="Add Equipment" value={newEquipment}
                  onChange={(e) => setNewEquipment(e.target.value)} placeholder="e.g., Pioneer DDJ-1000" />
                <Button variant="contained" sx={{ ml: 1 }} onClick={handleAddEquipment} disabled={!newEquipment.trim()}><Add /></Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {profile.equipment.map((item, index) => (
                  <Chip key={index} label={item} onDelete={() => handleRemoveEquipment(index)} />
                ))}
              </Box>
            </Box>

            {/* Featured Tracks */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Featured Tracks</Typography>
              <Box sx={{ display: 'flex', mb: 1, gap: 1 }}>
                <TextField size="small" label="Track Title" name="title" value={newTrack.title} onChange={handleTrackChange} sx={{ flex: 1 }} />
                <TextField size="small" label="URL (SoundCloud, etc.)" name="url" value={newTrack.url} onChange={handleTrackChange} sx={{ flex: 2 }} />
                <Button variant="contained" onClick={handleAddTrack} disabled={!newTrack.title.trim() || !newTrack.url.trim()}><Add /></Button>
              </Box>
              <Stack spacing={1}>
                {profile.featuredTracks.map((track, index) => (
                  <Paper key={index} sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MusicNote sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>{track.title}</Typography>
                      <Link href={track.url} target="_blank" sx={{ flex: 2, mx: 1 }}>{track.url}</Link>
                      <IconButton size="small" onClick={() => handleRemoveTrack(index)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Social Media Links */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>Social Media Links</Typography>
            <TextField fullWidth label="Instagram" name="social_instagram" value={profile.socialLinks.instagram} onChange={handleProfileChange}
              margin="dense" variant="outlined" InputProps={{ startAdornment: <Instagram sx={{ mr: 1, color: '#E1306C' }} fontSize="small" /> }} placeholder="Your Instagram username" />
            <TextField fullWidth label="Twitter" name="social_twitter" value={profile.socialLinks.twitter} onChange={handleProfileChange}
              margin="dense" variant="outlined" InputProps={{ startAdornment: <Twitter sx={{ mr: 1, color: '#1DA1F2' }} fontSize="small" /> }} placeholder="Your Twitter/X username" />
            <TextField fullWidth label="Facebook" name="social_facebook" value={profile.socialLinks.facebook} onChange={handleProfileChange}
              margin="dense" variant="outlined" InputProps={{ startAdornment: <Facebook sx={{ mr: 1, color: '#4267B2' }} fontSize="small" /> }} placeholder="Your Facebook page URL" />
            <TextField fullWidth label="SoundCloud" name="social_soundcloud" value={profile.socialLinks.soundcloud} onChange={handleProfileChange}
              margin="dense" variant="outlined" InputProps={{ startAdornment: <MusicNote sx={{ mr: 1, color: '#FF5500' }} fontSize="small" /> }} placeholder="Your SoundCloud URL" />
            <TextField fullWidth label="Mixcloud" name="social_mixcloud" value={profile.socialLinks.mixcloud} onChange={handleProfileChange}
              margin="dense" variant="outlined" InputProps={{ startAdornment: <MusicNote sx={{ mr: 1, color: '#5000FF' }} fontSize="small" /> }} placeholder="Your Mixcloud URL" />
            <TextField fullWidth label="Website" name="social_website" value={profile.socialLinks.website} onChange={handleProfileChange}
              margin="dense" variant="outlined" InputProps={{ startAdornment: <Language sx={{ mr: 1 }} fontSize="small" /> }} placeholder="Your personal website URL" />
          </Grid>

          {/* Legacy Availability section - hidden */}
          <Grid item xs={12} md={6} sx={{ display: 'none' }}>
            <Typography variant="subtitle2" gutterBottom>Simple Availability</Typography>
            <FormGroup row>
              {Object.keys(profile.availability).map((day) => (
                <FormControlLabel key={day} control={<Checkbox checked={profile.availability[day]} onChange={handleProfileChange} name={`availability_${day}`} />} />
              ))}
            </FormGroup>
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button variant="contained" color="primary" size="large" startIcon={<Save />} onClick={handleSaveProfile}>
          Save Profile
        </Button>
      </Box>
    </Paper>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'overview': return renderOverview();
      case 'stream': return renderStream();
      case 'schedule': return renderSchedule();
      case 'profile': return renderProfile();
      default: return renderOverview();
    }
  };

  return (
    <>
      <DashboardLayout
        title="DJ Dashboard"
        subtitle="Manage your profile and streaming"
        sections={SECTIONS}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      >
        {renderActiveSection()}
      </DashboardLayout>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog} onClose={handleCloseScheduleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex >= 0 ? 'Edit Schedule' : 'Add New Schedule'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add your regular gig schedule to help venues find you when they need DJs.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete fullWidth freeSolo loading={venueLoading} options={venueOptions}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                inputValue={venueSearchQuery}
                onInputChange={(event, newInputValue) => { handleVenueSearch(newInputValue); setScheduleEntry(prev => ({ ...prev, venue: newInputValue })); }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue !== 'string') handleVenueSelect(newValue);
                  else if (typeof newValue === 'string') setScheduleEntry(prev => ({ ...prev, venue: newValue, venueId: '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Venue Name" required InputProps={{ ...params.InputProps,
                    endAdornment: (<>{venueLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>)
                  }} />
                )}
                renderOption={(props, option) => (
                  <li {...props}><Box sx={{ display: 'flex', alignItems: 'center' }}><LocationOn fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />{option.name}</Box></li>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Day</InputLabel>
                <Select name="day" value={scheduleEntry.day} onChange={handleScheduleChange} label="Day">
                  {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                    <MenuItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Time" name="startTime" value={scheduleEntry.startTime} onChange={handleScheduleChange} placeholder="e.g. 8:00 PM" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Time" name="endTime" value={scheduleEntry.endTime} onChange={handleScheduleChange} placeholder="e.g. 2:00 AM" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" name="notes" value={scheduleEntry.notes} onChange={handleScheduleChange} multiline rows={2} placeholder="Any additional details about this gig..." />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
          <Button onClick={handleSaveSchedule} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled" elevation={6}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DJDashboard;
